// (moved into AtelierFilterBar)
// Collection Atelier - Infinite Scroll
const __atelierCache = (() => {
	// Small caching layer for grid payloads to reduce refetching.
	// Memory cache for speed + sessionStorage for back/forward and reloads.
	const MEM = new Map();
	const PREFIX = 'atelier_plp_cache_v1:';
	const INDEX_KEY = PREFIX + '__index__';
	const MAX_ENTRIES = 30;
	const TTL_MS = 15 * 60 * 1000; // 15 minutes

	const now = () => Date.now();
	const safeJsonParse = (s) => {
		try { return JSON.parse(s); } catch (_) { return null; }
	};

	const loadIndex = () => safeJsonParse(sessionStorage.getItem(INDEX_KEY) || '[]') || [];
	const saveIndex = (idx) => {
		try { sessionStorage.setItem(INDEX_KEY, JSON.stringify(idx.slice(0, MAX_ENTRIES))); } catch (_) {}
	};

	const key = (kind, urlString) => `${PREFIX}${kind}:${urlString}`;

	const get = (k) => {
		const hit = MEM.get(k);
		if (hit && (now() - hit.t) < TTL_MS) return hit.v;
		if (hit) MEM.delete(k);

		try {
			const raw = sessionStorage.getItem(k);
			if (!raw) return null;
			const parsed = safeJsonParse(raw);
			if (!parsed || !parsed.t) return null;
			if ((now() - parsed.t) >= TTL_MS) {
				sessionStorage.removeItem(k);
				return null;
			}
			MEM.set(k, parsed);
			return parsed.v;
		} catch (_) {
			return null;
		}
	};

	const prune = () => {
		let idx = loadIndex();
		// Remove missing/expired
		idx = idx.filter((k) => {
			try {
				const raw = sessionStorage.getItem(k);
				if (!raw) return false;
				const parsed = safeJsonParse(raw);
				if (!parsed?.t) return false;
				if ((now() - parsed.t) >= TTL_MS) {
					sessionStorage.removeItem(k);
					return false;
				}
				return true;
			} catch (_) { return false; }
		});
		// Cap
		if (idx.length > MAX_ENTRIES) {
			const drop = idx.slice(MAX_ENTRIES);
			drop.forEach((k) => { try { sessionStorage.removeItem(k); } catch (_) {} });
			idx = idx.slice(0, MAX_ENTRIES);
		}
		saveIndex(idx);
	};

	const set = (k, v) => {
		const entry = { t: now(), v };
		MEM.set(k, entry);
		try {
			sessionStorage.setItem(k, JSON.stringify(entry));
			const idx = loadIndex();
			const next = [k, ...idx.filter((x) => x !== k)];
			saveIndex(next);
			prune();
		} catch (_) {
			// If storage is full (QuotaExceeded), keep memory cache only.
		}
	};

	return { key, get, set };
})();

const getAtelierGridSectionId = () => {
	const grid = document.querySelector('.atelier-grid');
	return grid?.dataset.sectionId || 'collection-atelier-grid';
};

const isAtelierInfiniteEnabled = () => {
	const root = document.querySelector('.atelier-collection');
	return root?.dataset.atelierInfinite === 'true';
};

class AtelierInfiniteScroll {
	constructor() {
		this.grid = document.querySelector('.atelier-grid');
		this.loadMore = document.querySelector('.atelier-load-more');
		this.endMessage = document.querySelector('.atelier-end-message');
		this.backToTop = document.querySelector('.atelier-back-to-top');
		this.currentPage = parseInt(this.grid?.dataset.currentPage || '1', 10) || 1;
		this.totalPages = parseInt(this.grid?.dataset.totalPages || '1', 10) || 1;
		this.totalResults = parseInt(this.grid?.dataset.totalResults || '0', 10) || 0;
		this.isLoading = false;
		this.observer = null;
		this.backToTopBound = false;
		this.backToTopWatcherSet = false;

		// Keep the DOM size bounded for smooth scrolling.
		this.itemsPerPage = 48;
		this.keepPagesInDom = 3; // keep N pages of items in the DOM at any time

		// Phase 7 / P7-B2 (KI-PLP-2) bidirectional windowing state.
		// minLoadedPage tracks the LOWEST page number currently in the DOM,
		// so loadPrevPage knows which page to prepend when the user scrolls
		// back up past the trim threshold. On a fresh load this equals
		// currentPage (page 1 typically); after `loadNextPage` + `trimGridWindow`
		// has trimmed the oldest pages, minLoadedPage rises (e.g. 2, 3, ...);
		// after `loadPrevPage` it falls back toward 1.
		this.minLoadedPage = this.currentPage;
		// Sentinel + observer for "load previous page" (mirror of loadMore).
		// Created lazily in init() so we don't touch the Liquid section.
		this.loadPrev = null;
		this.prevObserver = null;

		// Phase 7 follow-up: floating progress pill state.
		// `maxViewedPage` is a monotonic high-water mark of the highest page
		// the customer has ever scrolled into — this means the pill count
		// only ever grows, even when bottom-trim drops `currentPage` after
		// a `loadPrevPage`. Without this, the pill would show "192 of 243"
		// after they scroll up, which is confusing because they HAVE
		// already viewed 240 items.
		this.maxViewedPage = this.currentPage;
		this.progressPill = null;
		this._progressPillScrollBound = false;

		// URL sync (replaceState) so share/refresh preserves scroll progress.
		this._urlSyncBound = false;
		this._urlSyncTicking = false;
		this._lastUrlPage = null;
		this._isBootstrapping = false;

		// Cancel in-flight requests to avoid overlaps/races
		this._abortController = null;
	}

	abortPending() {
		try {
			this._abortController?.abort();
		} catch (_) {}
		this._abortController = null;
	}

	init() {
		// Re-query in case the grid was replaced/updated (filters)
		this.grid = document.querySelector('.atelier-grid');
		this.currentPage = parseInt(this.grid?.dataset.currentPage || '1', 10) || 1;
		this.totalPages = parseInt(this.grid?.dataset.totalPages || '1', 10) || 1;
		this.totalResults = parseInt(this.grid?.dataset.totalResults || '0', 10) || 0;

		// Tag existing items with their source page for URL sync + windowing.
		this.tagGridItems(this.currentPage);
		this.bindUrlSync();

		// Phase 7 / P7-B3: bind save triggers FIRST so any quick repeat
		// click (e.g. user immediately clicks another card after restore)
		// gets captured.
		this.bindStateSaveTriggers();

		// Phase 7 / P7-B3: when arriving via PDP back-navigation, attempt
		// to restore the prior scroll + loaded page window. The restore
		// internally awaits bootstrapToUrlPage with suppressScroll so the
		// only thing we lose vs. a normal load is the boundary
		// scrollIntoView (which the saved scrollY replaces). If restore
		// can't run (no signal, no snapshot, URL changed, expired), fall
		// through to the standard URL-driven bootstrap.
		if (this.shouldAttemptRestore()) {
			this.tryRestorePLPState().then((restored) => {
				if (!restored) this.bootstrapToUrlPage();
			});
		} else {
			this.bootstrapToUrlPage();
		}

		// Phase 7 / P7-B2: derive minLoadedPage from existing items so we know
		// the lowest page currently in DOM (for loadPrevPage). After bootstrap
		// or a filter reset, this ensures we never re-fetch a page already
		// rendered server-side.
		this.minLoadedPage = this.computeMinLoadedPage();
		// Reset the high-water mark to currentPage on init/filter reset.
		// Avoids stale "200 of 39" pill state after a narrowing filter Apply.
		this.maxViewedPage = this.currentPage;

		// Ensure the trigger is visible so the observer can fire initially
		if (this.loadMore) {
			this.loadMore.style.display = 'block';
			this.loadMore.classList.add('is-hidden'); // hidden but keeps layout
		}

		if (this.loadMore && this.totalPages > 1) {
			this.observer = new IntersectionObserver((entries) => {
				entries.forEach(entry => {
					if (entry.isIntersecting && !this.isLoading) {
						this.loadNextPage();
					}
				});
			}, { rootMargin: '1200px 0px' });

			this.observer.observe(this.loadMore);
		}

		// Phase 7 / P7-B2: bidirectional windowing — set up the "load previous"
		// sentinel + observer. Created once (idempotent) and kept around even
		// when minLoadedPage <= 1; we just hide it via .is-hidden in that case.
		this.ensureLoadPrevSentinel();
		this.updateLoadPrevVisibility();

		// Phase 7 / P7-C7: floating actions wrapper (must come BEFORE the
		// pill so the pill can be placed inside it). Both back-to-top and
		// the progress pill end up as flex siblings centered at the bottom.
		this.ensureFloatingActions();
		// Phase 7 follow-up: floating progress pill (mirror of back-to-top).
		this.ensureProgressPill();
		this.bindProgressPillScroll();
		this.updateProgressPill();
	}

// (moved into AtelierFilterBar)

	// Phase 7 / P7-B2: derive the minimum page number currently in the DOM.
	// Falls back to currentPage when no items are tagged.
	computeMinLoadedPage() {
		if (!this.grid) return this.currentPage || 1;
		const items = this.grid.querySelectorAll(':scope > li');
		if (!items.length) return this.currentPage || 1;
		let min = Infinity;
		items.forEach((li) => {
			const p = parseInt(li.dataset.page || '1', 10) || 1;
			if (p < min) min = p;
		});
		return Number.isFinite(min) ? min : (this.currentPage || 1);
	}

	// Phase 7 / P7-B2: lazily create the "load previous" sentinel + observer.
	// Idempotent — safe to call multiple times (e.g. after resetInfiniteScroll
	// when the grid is rebuilt by a filter Apply).
	ensureLoadPrevSentinel() {
		if (!this.grid) return;
		// If sentinel exists but was orphaned by a grid replace, re-attach.
		const existing = document.querySelector('.atelier-load-prev');
		if (existing) {
			this.loadPrev = existing;
		} else {
			const el = document.createElement('div');
			el.className = 'atelier-load-prev is-hidden';
			el.setAttribute('aria-hidden', 'true');
			el.innerHTML = '<div class="atelier-loading-spinner"><div class="spinner"></div><span>Loading earlier products...</span></div>';
			this.grid.parentNode.insertBefore(el, this.grid);
			this.loadPrev = el;
		}

		// Reset and (re)create the observer so it watches the current sentinel
		// instance after any DOM rewrites.
		if (this.prevObserver) {
			try { this.prevObserver.disconnect(); } catch (_) {}
			this.prevObserver = null;
		}
		this.prevObserver = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting && !this.isLoading && this.minLoadedPage > 1) {
					this.loadPrevPage();
				}
			});
		}, { rootMargin: '1200px 0px' });
		this.prevObserver.observe(this.loadPrev);
	}

	// Phase 7 / P7-C7: wrap back-to-top + progress pill in a single flex
	// container positioned at the bottom-CENTER of the viewport. Both
	// children become inline siblings with equal heights via `align-items:
	// stretch`. JS-moves the existing back-to-top button (server-rendered
	// in section markup) into the wrapper. Idempotent — safe to call
	// multiple times, e.g. after a filter Apply rebuilds the section.
	ensureFloatingActions() {
		let wrapper = document.querySelector('.atelier-floating-actions');
		if (!wrapper) {
			wrapper = document.createElement('div');
			wrapper.className = 'atelier-floating-actions';
			document.body.appendChild(wrapper);
		}
		this.floatingActions = wrapper;
		// Re-host the back-to-top button if it lives elsewhere (initial
		// section render places it as a child of the section). Order
		// matters: pill on the LEFT, back-to-top on the RIGHT.
		if (this.backToTop && this.backToTop.parentNode !== wrapper) {
			wrapper.appendChild(this.backToTop);
		}
	}

	// Phase 7 follow-up: lazily create the floating progress pill that
	// sits next to the back-to-top button. JS-injected (no Liquid touch),
	// idempotent so it survives section refreshes via filter Apply.
	// Phase 7 / P7-C7: the pill is now hosted inside the floating-actions
	// wrapper so both elements share a single bottom-center position.
	ensureProgressPill() {
		const existing = document.querySelector('.atelier-progress-pill');
		if (existing) {
			this.progressPill = existing;
		} else {
			const pill = document.createElement('div');
			pill.className = 'atelier-progress-pill';
			pill.setAttribute('aria-live', 'polite');
			pill.setAttribute('aria-atomic', 'true');
			pill.setAttribute('role', 'status');
			pill.innerHTML = '<span class="progress-spinner" aria-hidden="true"></span><span class="progress-text"></span>';
			this.progressPill = pill;
		}
		// Place pill BEFORE the back-to-top inside the wrapper so layout
		// reads "[ N of X ] [ Back to Top ]" left-to-right.
		const wrapper = this.floatingActions || document.querySelector('.atelier-floating-actions');
		if (wrapper) {
			if (this.backToTop && this.backToTop.parentNode === wrapper) {
				wrapper.insertBefore(this.progressPill, this.backToTop);
			} else {
				wrapper.appendChild(this.progressPill);
			}
		} else if (!this.progressPill.parentNode) {
			document.body.appendChild(this.progressPill);
		}
	}

	// Phase 7 follow-up: hook the progress pill into the existing scroll
	// stream so its visibility tracks the back-to-top button.
	bindProgressPillScroll() {
		if (this._progressPillScrollBound) return;
		this._progressPillScrollBound = true;
		window.addEventListener('scroll', () => this.updateProgressPill(), { passive: true });
	}

	// Phase 7 follow-up: refresh pill text + spinner state + visibility.
	// Called from: init, scroll, loadNextPage success, loadPrevPage success,
	// bootstrapToUrlPage, showLoading/hideLoading, updateBackToTopVisibility.
	updateProgressPill() {
		if (!this.progressPill) return;

		const total = this.totalResults || 0;
		// Use itemsPerPage (learned MAXIMUM from P7-B1) capped at total.
		// On a fresh load with no AJAX yet, itemsPerPage = 48 (default).
		const perPage = this.itemsPerPage || 48;
		const viewedRaw = (this.maxViewedPage || 1) * perPage;
		const viewed = total > 0 ? Math.min(viewedRaw, total) : 0;

		const text = this.progressPill.querySelector('.progress-text');
		if (text) {
			text.textContent = total > 0 ? `${viewed} of ${total}` : '';
		}

		// Loading state: spinner shown via CSS when .is-loading is set.
		if (this.isLoading) {
			this.progressPill.classList.add('is-loading');
		} else {
			this.progressPill.classList.remove('is-loading');
		}

		// Visibility: synced with the back-to-top button per merchant request.
		// Show only when (a) the user has scrolled past the threshold AND (b)
		// they're past page 1 — same gate as updateBackToTopVisibility.
		const threshold = 200;
		const visible = this.currentPage >= 2 && window.scrollY > threshold;
		if (visible && total > 0) {
			this.progressPill.classList.add('is-visible');
		} else {
			this.progressPill.classList.remove('is-visible');
		}
	}

	// Phase 7 / P7-B2: show/hide the prev sentinel based on minLoadedPage.
	// Uses the existing .is-hidden pattern (visibility: hidden — preserves
	// layout space so the IntersectionObserver still fires).
	updateLoadPrevVisibility() {
		if (!this.loadPrev) return;
		if (this.minLoadedPage > 1) {
			this.loadPrev.classList.remove('is-hidden');
			this.loadPrev.removeAttribute('aria-hidden');
		} else {
			this.loadPrev.classList.add('is-hidden');
			this.loadPrev.setAttribute('aria-hidden', 'true');
		}
	}

	// Phase 7 / P7-B2: fetch the page just below minLoadedPage and prepend it
	// to the grid, keeping the user's viewport anchored on what they were
	// already looking at. Mirrors loadNextPage but for upward scroll.
	async loadPrevPage() {
		if (this.isLoading || this.minLoadedPage <= 1) return;

		this.abortPending();
		this._abortController = new AbortController();
		this.isLoading = true;

		try {
			const prevPage = this.minLoadedPage - 1;
			const url = new URL(window.location.href);
			url.searchParams.set('page', String(prevPage));
			url.searchParams.set('section_id', getAtelierGridSectionId());

			const cacheKey = __atelierCache.key('section', url.toString());
			let html = __atelierCache.get(cacheKey);
			if (!html) {
				const res = await fetch(url.toString(), { credentials: 'same-origin', signal: this._abortController.signal });
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				html = await res.text();
				__atelierCache.set(cacheKey, html);
			}

			const doc = new DOMParser().parseFromString(html, 'text/html');
			const newProducts = doc.querySelectorAll('.atelier-grid > li');
			if (newProducts.length === 0) {
				// Nothing to prepend — guard so we don't get stuck.
				this.minLoadedPage = Math.max(1, this.minLoadedPage - 1);
				this.updateLoadPrevVisibility();
				return;
			}

			// Same grow-only itemsPerPage learned-maximum (P7-B1).
			if (newProducts.length > this.itemsPerPage) {
				this.itemsPerPage = newProducts.length;
			}

			const frag = document.createDocumentFragment();
			newProducts.forEach((product) => {
				const clone = product.cloneNode(true);
				clone.dataset.page = String(prevPage);
				frag.appendChild(clone);
			});

			// Anchor the viewport: measure document height before/after the
			// prepend and scroll by the delta so the user keeps looking at
			// the same item rather than getting yanked upward.
			const oldHeight = document.documentElement.scrollHeight;
			this.grid.insertBefore(frag, this.grid.firstChild);
			const newHeight = document.documentElement.scrollHeight;
			const delta = newHeight - oldHeight;
			if (delta > 0) {
				window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
			}

			this.minLoadedPage = prevPage;

			// If prepending pushed us over the budget, trim from the bottom
			// (items below the viewport — no scroll shift required).
			this.trimGridWindowBottom();

			// Trimming the bottom may have decreased currentPage. If we were
			// at totalPages (end-of-collection state), re-enable the next-page
			// observer + hide end message so further downward scrolls work.
			this.restartNextObserverIfNeeded();

			this.updateLoadPrevVisibility();
			this.updateProgressPill();
			this.syncUrlToVisiblePage(true);
		} catch (error) {
			if (error && (error.name === 'AbortError' || String(error).includes('AbortError'))) {
				return;
			}
			console.error('Error loading previous page:', error);
		} finally {
			this.isLoading = false;
			this._abortController = null;
			// Reflect post-fetch state in the pill (clears the spinner).
			this.updateProgressPill();
		}
	}

	// Phase 7 / P7-B2: trim from the END of the grid when prepending pushes
	// total items over `itemsPerPage * keepPagesInDom`. Mirrors
	// `trimGridWindow` but operates on the tail. No scroll compensation is
	// needed because removing items below the viewport doesn't shift it.
	trimGridWindowBottom() {
		if (!this.grid) return;
		const maxItems = this.itemsPerPage * this.keepPagesInDom;
		const items = Array.from(this.grid.querySelectorAll(':scope > li'));
		if (items.length <= maxItems) return;

		const removeCount = items.length - maxItems;
		const toRemove = items.slice(items.length - removeCount);
		toRemove.forEach((li) => li.remove());

		// Recompute currentPage from the now-last item so loadNextPage knows
		// where to resume when the user scrolls back down.
		const remaining = this.grid.querySelectorAll(':scope > li');
		if (remaining.length > 0) {
			const lastPage = parseInt(remaining[remaining.length - 1].dataset.page || '1', 10) || 1;
			this.currentPage = lastPage;
			this.grid.dataset.currentPage = String(lastPage);
		}
	}

	// Phase 7 / P7-B2: when bottom-trimming brings currentPage below
	// totalPages (or we were previously at end-of-collection), re-enable the
	// next-page IntersectionObserver and hide the end-message banner so the
	// user can scroll down through the same pages again seamlessly.
	restartNextObserverIfNeeded() {
		if (!this.loadMore) return;
		if (this.currentPage >= this.totalPages) return; // still at end
		// Hide end-message if it was shown.
		if (this.endMessage) this.endMessage.style.display = 'none';
		// Restore the load-more trigger to the layout so the observer can fire.
		this.loadMore.style.display = 'block';
		this.loadMore.classList.add('is-hidden');
		// Recreate observer if it was disconnected at end-of-collection.
		if (!this.observer) {
			this.observer = new IntersectionObserver((entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting && !this.isLoading) {
						this.loadNextPage();
					}
				});
			}, { rootMargin: '1200px 0px' });
		}
		// .observe is idempotent for the same target — safe to call.
		this.observer.observe(this.loadMore);
	}

	async loadNextPage() {
		if (this.isLoading || this.currentPage >= this.totalPages) return;

		// Cancel any previous request (safety)
		this.abortPending();
		this._abortController = new AbortController();

		this.isLoading = true;
		this.showLoading();

		try {
			const nextPage = this.currentPage + 1;
			const url = new URL(window.location.href);
			url.searchParams.set('page', String(nextPage));
			// Fetch only the grid markup (Section Rendering API) to avoid downloading/parsing the full page HTML
			url.searchParams.set('section_id', getAtelierGridSectionId());

			const cacheKey = __atelierCache.key('section', url.toString());
			let html = __atelierCache.get(cacheKey);
			if (!html) {
				const response = await fetch(url.toString(), { credentials: 'same-origin', signal: this._abortController.signal });
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				html = await response.text();
				__atelierCache.set(cacheKey, html);
			}
			const parser = new DOMParser();
			const doc = parser.parseFromString(html, 'text/html');

			const newProducts = doc.querySelectorAll('.atelier-grid > li');
			
			if (newProducts.length > 0) {
				// Phase 7 / P7-B1 (KI-PLP-1): never let `itemsPerPage` shrink.
				// The previous logic (`this.itemsPerPage = newProducts.length`) overwrote
				// itemsPerPage with the size of the most recent fetched page. On the LAST
				// page of a collection (which is almost always partial — e.g. 5 items in a
				// 197-product collection where pages 1-4 had 48 each), itemsPerPage became
				// 5 and `trimGridWindow` then computed `maxItems = 5 * 3 = 15`, aggressively
				// trimming the grid down to 15 items and wiping out everything the customer
				// had scrolled through. Treat itemsPerPage as a learned MAXIMUM instead, so
				// the windowing budget stays at `48 * 3 = 144` (or whatever the real page
				// size is) even when a partial last page lands.
				if (newProducts.length > this.itemsPerPage) {
					this.itemsPerPage = newProducts.length;
				}

				const frag = document.createDocumentFragment();
				newProducts.forEach(product => {
					const clone = product.cloneNode(true);
					clone.dataset.page = String(nextPage);
					frag.appendChild(clone);
				});
				this.grid.appendChild(frag);

				this.currentPage = nextPage;
				this.grid.dataset.currentPage = String(this.currentPage);
				if (this.currentPage > this.maxViewedPage) {
					this.maxViewedPage = this.currentPage;
				}

				this.trimGridWindow();
				// Phase 7 / P7-B2: trimming the top may have raised
				// minLoadedPage; recompute and refresh the prev sentinel so
				// scrolling back up triggers loadPrevPage.
				this.minLoadedPage = this.computeMinLoadedPage();
				this.updateLoadPrevVisibility();
				this.updateProgressPill();
				this.syncUrlToVisiblePage(true);

				// Phase 7 / P7-C7: H1 .results-count is now server-rendered with
				// `collection.all_products_count` and stays at the full collection
				// total. The floating progress pill carries the per-load progress.

				// Update load progress under spinner
				const loadProgress = document.querySelector('.atelier-loading-spinner .load-progress');
				if (loadProgress) {
					const viewedCount = Math.min(this.currentPage * 48, this.totalResults);
					loadProgress.textContent = `${viewedCount} of ${this.totalResults}`;
				}
				// Announce infinite scroll load
				const announce = document.getElementById('atelier-announce');
				if (announce) {
					const viewedCount = Math.min(this.currentPage * 48, this.totalResults);
					announce.textContent = `Loaded more items: ${viewedCount} of ${this.totalResults}`;
				}

				// Show back-to-top after first auto-load
				if (this.currentPage >= 2) {
					this.showBackToTop();
				}

				// If last page reached, finalize
				if (this.currentPage >= this.totalPages) {
					this.hideLoading(true); // fully hide
					this.showEndMessage();
					if (this.observer) this.observer.disconnect();
				} else {
					// Keep trigger in layout for next intersection
					this.hideLoading(false);
				}
			} else {
				this.hideLoading(true);
				this.showEndMessage();
				if (this.observer) this.observer.disconnect();
			}
		} catch (error) {
			if (error && (error.name === 'AbortError' || String(error).includes('AbortError'))) {
				// Swallow aborts (a newer request took over)
				this.hideLoading(false);
				return;
			}
			console.error('Error loading next page:', error);
			this.hideLoading(false);
		} finally {
			this.isLoading = false;
			this._abortController = null;
			// Reflect post-fetch state in the floating pill (clears spinner).
			this.updateProgressPill();
		}
	}

	// Phase 7 / P7-B3: `suppressScroll` lets the PDP back-nav restore
	// path drive its own `scrollTo(savedScrollY)` after bootstrap finishes,
	// without bootstrap stomping it via the boundary `scrollIntoView`.
	async bootstrapToUrlPage({ suppressScroll = false } = {}) {
		if (!this.grid) return;

		const currentUrl = new URL(window.location.href);
		const targetPage = parseInt(currentUrl.searchParams.get('page') || '1', 10) || 1;

		// If no page is specified (or it's page 1), just ensure URL reflects what is visible.
		if (targetPage <= 1) {
			this.syncUrlToVisiblePage(true);
			return;
		}

		// Load up to (keepPagesInDom-1) previous pages so the link lands at the right position.
		// Example: page=4 with keepPagesInDom=3 → load pages 2,3,4 and scroll to start of page 4.
		const startPage = Math.max(1, targetPage - (this.keepPagesInDom - 1));

		this._isBootstrapping = true;
		this.isLoading = true; // prevent observer-triggered loads during bootstrap
		this.abortPending();
		this._abortController = new AbortController();

		try {
			const pageNums = [];
			for (let p = startPage; p <= targetPage; p++) pageNums.push(p);

			const fetchOne = async (p) => {
				const u = new URL(window.location.href);
				u.searchParams.set('page', String(p));
				u.searchParams.set('section_id', getAtelierGridSectionId());
				const cacheKey = __atelierCache.key('section', u.toString());
				let html = __atelierCache.get(cacheKey);
				if (!html) {
					const res = await fetch(u.toString(), { credentials: 'same-origin', signal: this._abortController.signal });
					if (!res.ok) return [];
					html = await res.text();
					__atelierCache.set(cacheKey, html);
				}
				const doc = new DOMParser().parseFromString(html, 'text/html');
				const lis = Array.from(doc.querySelectorAll('.atelier-grid > li'));
				// Phase 7 / P7-B1: same fix as loadNextPage above. Treat itemsPerPage
				// as a learned MAXIMUM so a partial last page in the bootstrap window
				// (e.g. landing on `?page=4` when page 4 is partial) doesn't shrink the
				// trim budget and immediately wipe out pages 2-3 after bootstrap completes.
				if (lis.length > this.itemsPerPage) {
					this.itemsPerPage = lis.length;
				}
				return lis.map((li) => {
					const clone = li.cloneNode(true);
					clone.dataset.page = String(p);
					return clone;
				});
			};

			const pages = await Promise.all(pageNums.map(fetchOne));
			const frag = document.createDocumentFragment();
			pages.forEach((lis) => lis.forEach((li) => frag.appendChild(li)));

			// Replace grid contents with our bootstrap window.
			this.grid.innerHTML = '';
			this.grid.appendChild(frag);

			// Track the highest + lowest pages currently loaded.
			this.currentPage = targetPage;
			this.grid.dataset.currentPage = String(targetPage);
			if (this.currentPage > this.maxViewedPage) {
				this.maxViewedPage = this.currentPage;
			}
			// Phase 7 / P7-B2: record the bottom of the bootstrap window so
			// scrolling further back up can fetch pages 1..startPage-1.
			this.minLoadedPage = startPage;
			this.updateLoadPrevVisibility();
			this.updateProgressPill();

			// Jump to the boundary so the shared link "lands" at the intended spot.
			// Phase 7 / P7-B3: skipped when the restore path will set its own scrollY.
			requestAnimationFrame(() => {
				if (!suppressScroll) {
					const anchor = this.grid.querySelector(`:scope > li[data-page="${targetPage}"]`);
					if (anchor && typeof anchor.scrollIntoView === 'function') {
						anchor.scrollIntoView({ block: 'start', behavior: 'auto' });
					}
				}
				this.isLoading = false;
				this._isBootstrapping = false;
				this.syncUrlToVisiblePage(true);
			});
		} catch (e) {
			if (e && (e.name === 'AbortError' || String(e).includes('AbortError'))) {
				this.isLoading = false;
				this._isBootstrapping = false;
				this._abortController = null;
				return;
			}
			this.isLoading = false;
			this._isBootstrapping = false;
			this._abortController = null;
			this.syncUrlToVisiblePage(true);
			return;
		} finally {
			this._abortController = null;
		}
	}

	tagGridItems(pageNumber = 1) {
		if (!this.grid) return;
		const page = parseInt(pageNumber, 10) || 1;
		this.grid.querySelectorAll(':scope > li').forEach((li) => {
			if (!li.dataset.page) li.dataset.page = String(page);
		});
	}

	// Phase 7 / P7-B3: Save / restore PLP scroll + loaded-page state across
	// PDP back-navigation. Resolves KI-PLP-3.
	//
	// Flow:
	//   1. User scrolls to page 4 of /collections/X (DOM has pages 2,3,4
	//      after windowing trim).
	//   2. User clicks a product card → savePLPState() captures
	//      { url, scrollY, loadedPages, currentPage, minLoadedPage,
	//        maxViewedPage, timestamp } into sessionStorage.
	//   3. Browser navigates to /products/X.
	//   4. User clicks browser BACK.
	//   5. PLP loads. shouldAttemptRestore() returns true (Performance
	//      Navigation Timing reports `back_forward` OR document.referrer
	//      starts with /products/).
	//   6. tryRestorePLPState() validates the snapshot (URL match excluding
	//      `?page=`, < 30 min old), runs bootstrapToUrlPage with
	//      suppressScroll=true to load the right window of pages, then
	//      restores the saved scrollY + maxViewedPage/minLoadedPage.
	//
	// Bfcache (Safari/Firefox/sometimes Chrome): browser preserves the
	// entire page including DOM, JS state, and scrollY. No restore action
	// needed — the pageshow listener exists for diagnostics only.

	canonicalUrlForState() {
		// Strip `?page=N` because the saved snapshot's loadedPages array
		// already encodes which pages we want; comparing URLs with `?page=`
		// would falsely invalidate snapshots whose loaded window differs
		// from the current URL's `?page=`.
		try {
			const url = new URL(window.location.href);
			url.searchParams.delete('page');
			return url.pathname + url.search;
		} catch (_) {
			return window.location.pathname;
		}
	}

	savePLPState() {
		if (!this.grid) return;
		const items = Array.from(this.grid.querySelectorAll(':scope > li'));
		if (items.length === 0) return;

		// Derive loadedPages from data-page attributes (deduped + sorted).
		const seen = new Set();
		items.forEach((li) => {
			const p = parseInt(li.dataset.page || '1', 10) || 1;
			seen.add(p);
		});
		const loadedPages = Array.from(seen).sort((a, b) => a - b);

		const snapshot = {
			url: this.canonicalUrlForState(),
			scrollY: window.scrollY || 0,
			minLoadedPage: this.minLoadedPage,
			currentPage: this.currentPage,
			maxViewedPage: this.maxViewedPage,
			loadedPages,
			timestamp: Date.now(),
		};

		try {
			sessionStorage.setItem('atelier_plp_state', JSON.stringify(snapshot));
		} catch (_) {
			// sessionStorage full or disabled — fail silently. The user
			// just won't get scroll restore on this back-nav. Not worth
			// surfacing an error.
		}
	}

	readSavedSnapshot() {
		try {
			const raw = sessionStorage.getItem('atelier_plp_state');
			if (!raw) return null;
			const snapshot = JSON.parse(raw);
			if (!snapshot || typeof snapshot !== 'object') return null;
			if (!snapshot.url || !Array.isArray(snapshot.loadedPages)) return null;
			// 30-minute TTL — if the user came back hours later, treat the
			// PLP as a fresh visit (matches Mejuri-style "session feel").
			if (Date.now() - (snapshot.timestamp || 0) > 30 * 60 * 1000) {
				sessionStorage.removeItem('atelier_plp_state');
				return null;
			}
			// URL match (canonical, ignoring `?page=`).
			if (snapshot.url !== this.canonicalUrlForState()) return null;
			return snapshot;
		} catch (_) {
			return null;
		}
	}

	referrerIsPDP() {
		if (!document.referrer) return false;
		try {
			const ref = new URL(document.referrer);
			return ref.origin === window.location.origin && ref.pathname.startsWith('/products/');
		} catch (_) {
			return false;
		}
	}

	shouldAttemptRestore() {
		// Two signals that we're returning from a PDP, either of which
		// alone is enough to attempt restore:
		//   1. Performance Navigation Timing reports `back_forward` (most
		//      reliable — Safari, Chrome, Firefox).
		//   2. document.referrer is a same-origin /products/ URL (covers
		//      cases where Performance API isn't available or returns
		//      `navigate`, e.g. some link-click flows).
		try {
			const navEntries = performance.getEntriesByType && performance.getEntriesByType('navigation');
			const navEntry = navEntries && navEntries[0];
			if (navEntry && navEntry.type === 'back_forward') return true;
		} catch (_) { /* swallow — fall back to referrer check */ }
		return this.referrerIsPDP();
	}

	async tryRestorePLPState() {
		const snapshot = this.readSavedSnapshot();
		if (!snapshot) return false;

		// Run the standard bootstrap to load the page window — but tell it
		// not to scroll, because we'll restore the EXACT saved scrollY.
		// bootstrapToUrlPage already handles `?page=N` → load
		// startPage..N where startPage = max(1, N - keepPagesInDom + 1),
		// which is the same window the snapshot recorded.
		await this.bootstrapToUrlPage({ suppressScroll: true });

		// Restore the high-water marks so the floating pill + prev sentinel
		// reflect the user's actual progress, not the freshly-bootstrapped
		// page count.
		this.maxViewedPage = Math.max(this.maxViewedPage, snapshot.maxViewedPage || 1);
		// minLoadedPage is already set by bootstrapToUrlPage; only override
		// if the snapshot remembers a wider window (defensive).
		if (snapshot.minLoadedPage && snapshot.minLoadedPage < this.minLoadedPage) {
			this.minLoadedPage = snapshot.minLoadedPage;
		}

		// Wait one frame for layout to settle after grid replacement, then
		// restore scroll. Without this, the document height may not yet
		// reflect the freshly-rendered grid and scrollY clamps to a wrong
		// value.
		requestAnimationFrame(() => {
			window.scrollTo({ top: snapshot.scrollY || 0, left: 0, behavior: 'auto' });
			this.updateLoadPrevVisibility();
			this.updateProgressPill();
			this.updateBackToTopVisibility();
		});

		return true;
	}

	bindStateSaveTriggers() {
		if (this._stateSaveBound) return;
		this._stateSaveBound = true;

		// Save before the user navigates AWAY to a PDP. Capture-phase so
		// our save runs even if a child element calls e.preventDefault.
		// Filter to clicks on product links inside the grid only — header
		// links, breadcrumbs, sub-collection chips don't need to save.
		document.addEventListener('click', (e) => {
			const link = e.target && e.target.closest && e.target.closest('a[href*="/products/"]');
			if (!link) return;
			if (!this.grid || !this.grid.contains(link)) return;
			this.savePLPState();
		}, true);

		// Belt-and-suspenders backup: any time the page is being unloaded
		// for real (not into bfcache), save state. Catches refresh, typed
		// URL navigation, tab close-then-restore, and any future code that
		// triggers navigation without going through a normal click.
		window.addEventListener('pagehide', (e) => {
			if (!e.persisted) {
				this.savePLPState();
			}
		});
	}

	getVisiblePage() {
		if (!this.grid) return 1;
		const items = Array.from(this.grid.querySelectorAll(':scope > li'));
		for (let i = 0; i < items.length; i++) {
			const r = items[i].getBoundingClientRect();
			if (r.bottom > 0) {
				const p = parseInt(items[i].dataset.page || '1', 10) || 1;
				return p;
			}
		}
		return 1;
	}

	syncUrlToVisiblePage(force = false) {
		if (this._isBootstrapping) return;
		const page = this.getVisiblePage();
		if (!force && this._lastUrlPage === page) return;
		this._lastUrlPage = page;

		// Update ?page= only (keep all other params like filters/sort)
		const url = new URL(window.location.href);
		if (page > 1) url.searchParams.set('page', String(page));
		else url.searchParams.delete('page');

		const next = url.toString();
		if (next !== window.location.href) {
			window.history.replaceState({ page }, '', next);
		}
	}

	bindUrlSync() {
		if (this._urlSyncBound) return;
		this._urlSyncBound = true;

		const onScroll = () => {
			if (this._urlSyncTicking) return;
			this._urlSyncTicking = true;
			requestAnimationFrame(() => {
				this._urlSyncTicking = false;
				this.syncUrlToVisiblePage(false);
			});
		};

		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onScroll, { passive: true });
	}

	getOuterHeight(el) {
		if (!el) return 0;
		const rect = el.getBoundingClientRect();
		const styles = window.getComputedStyle(el);
		const mt = parseFloat(styles.marginTop || '0') || 0;
		const mb = parseFloat(styles.marginBottom || '0') || 0;
		return rect.height + mt + mb;
	}

	trimGridWindow() {
		if (!this.grid) return;
		const maxItems = this.itemsPerPage * this.keepPagesInDom;
		const items = Array.from(this.grid.querySelectorAll(':scope > li'));
		if (items.length <= maxItems) return;

		const removeCount = items.length - maxItems;
		const toRemove = items.slice(0, removeCount);

		// Preserve scroll position using an anchor element near the viewport.
		// This is more reliable than summing heights (which can be wrong with grid/intrinsic sizing).
		let anchor = null;
		for (let i = removeCount; i < items.length; i++) {
			const r = items[i].getBoundingClientRect();
			if (r.bottom > 0) {
				anchor = items[i];
				break;
			}
		}
		if (!anchor) anchor = items[removeCount];
		if (!anchor) return;

		const anchorTopBefore = anchor.getBoundingClientRect().top;

		toRemove.forEach((li) => li.remove());

		const anchorTopAfter = anchor.getBoundingClientRect().top;
		const delta = anchorTopAfter - anchorTopBefore;

		// Phase 7 / P7-C1: round delta to integer + ignore sub-pixel jitter.
		// Sub-pixel deltas from getBoundingClientRect() (e.g. ~0.4px from
		// fractional grid heights) cause window.scrollBy to thrash on each
		// trim with no visible benefit. Round to whole px and only act when
		// the shift is >= 1px.
		const rounded = Math.round(delta);
		if (Math.abs(rounded) >= 1) {
			window.scrollBy({ top: rounded, left: 0, behavior: 'auto' });
		}
	}

	showLoading() {
		if (this.loadMore) {
			this.loadMore.classList.remove('is-hidden');
		}
		// Reflect loading state in the floating progress pill (spinner on).
		this.updateProgressPill();
	}

	hideLoading(fullyHide = false) {
		if (this.loadMore) {
			if (fullyHide) {
				this.loadMore.style.display = 'none';
			} else {
				this.loadMore.classList.add('is-hidden'); // keep layout
			}
		}
		// Reflect post-load state in the floating progress pill (spinner off).
		this.updateProgressPill();
	}

	showEndMessage() {
		if (this.endMessage) {
			// Update message with filtered total
			const p = this.endMessage.querySelector('p');
			if (p) {
				const count = this.totalResults || 0;
				p.textContent = `You've viewed all ${count} product${count === 1 ? '' : 's'} in this collection`;
			}
			this.endMessage.style.display = 'block';
		}
	}

	showBackToTop() {
		if (!this.backToTop) return;

		// Bind click once
		if (!this.backToTopBound) {
			this.backToTop.addEventListener('click', () => this.handleBackToTopClick());
			this.backToTopBound = true;
		}

		// Ensure visibility updates with scroll position
		this.updateBackToTopVisibility();
		if (!this.backToTopWatcherSet) {
			window.addEventListener('scroll', () => this.updateBackToTopVisibility());
			this.backToTopWatcherSet = true;
		}
	}

	// Phase 7 / P7-B2 follow-up: bidirectional windowing made plain
	// `scrollTo({ top: 0, behavior: 'smooth' })` unreliable here. As the
	// smooth scroll travels upward, the new prev sentinel triggers
	// loadPrevPage, which calls window.scrollBy(delta) for viewport
	// anchoring — that imperative scroll cancels the in-flight smooth
	// animation and strands the user mid-page.
	//
	// Fix: when we're past page 1 (i.e. minLoadedPage > 1 OR currentPage
	// has advanced), AJAX-reset the grid to a fresh page 1 first, then
	// instant-scroll to top. Cache-friendly (page 1 is almost always
	// already in __atelierCache) and gives the user a clean "back to
	// the very beginning" experience instead of "land somewhere near
	// the trim threshold."
	async handleBackToTopClick() {
		if (this.minLoadedPage > 1 || this.currentPage > 1) {
			try {
				await this.resetToPageOne();
			} catch (_) {
				// On reset failure, fall through to a plain scroll so the
				// click still does *something* useful.
			}
		}
		window.scrollTo({ top: 0, behavior: 'auto' });
		// Update floating UI after teleporting to top.
		this.updateBackToTopVisibility();
		if (typeof this.updateProgressPill === 'function') {
			this.updateProgressPill();
		}
	}

	// Phase 7 / P7-B2 follow-up: replace the grid contents with a fresh
	// page 1 fetch (Section Rendering API), preserving any active filter
	// URL params (we strip only `page=`). Updates state + URL + sentinels
	// so subsequent scrolls behave as if it's a fresh visit.
	async resetToPageOne() {
		if (!this.grid) return;
		this.abortPending();
		this._abortController = new AbortController();
		this.isLoading = true;

		try {
			const u = new URL(window.location.href);
			u.searchParams.delete('page');
			u.searchParams.set('section_id', getAtelierGridSectionId());

			const cacheKey = __atelierCache.key('section', u.toString());
			let html = __atelierCache.get(cacheKey);
			if (!html) {
				const res = await fetch(u.toString(), { credentials: 'same-origin', signal: this._abortController.signal });
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				html = await res.text();
				__atelierCache.set(cacheKey, html);
			}

			const doc = new DOMParser().parseFromString(html, 'text/html');
			const newGrid = doc.querySelector('.atelier-grid');
			if (!newGrid) return;

			this.grid.innerHTML = newGrid.innerHTML;
			if (newGrid.dataset.totalPages) this.grid.dataset.totalPages = newGrid.dataset.totalPages;
			if (newGrid.dataset.totalResults) this.grid.dataset.totalResults = newGrid.dataset.totalResults;
			this.grid.dataset.currentPage = '1';

			this.tagGridItems(1);
			this.currentPage = 1;
			this.minLoadedPage = 1;
			// Reset the pill's high-water mark so we don't show "240 of 243"
			// the moment the user scrolls into page 2 after a back-to-top
			// reset; the count should restart from page 1.
			this.maxViewedPage = 1;
			this.totalPages = parseInt(this.grid.dataset.totalPages || '1', 10) || 1;
			this.totalResults = parseInt(this.grid.dataset.totalResults || '0', 10) || 0;

			// Scrub `?page=` from the visible URL — we're at page 1 again.
			const cleanUrl = new URL(window.location.href);
			cleanUrl.searchParams.delete('page');
			if (cleanUrl.toString() !== window.location.href) {
				window.history.replaceState({}, '', cleanUrl.toString());
				this._lastUrlPage = 1;
			}

			this.updateLoadPrevVisibility();
			this.restartNextObserverIfNeeded();
		} catch (e) {
			if (e && (e.name === 'AbortError' || String(e).includes('AbortError'))) return;
			console.error('Error resetting to page 1:', e);
			throw e;
		} finally {
			this.isLoading = false;
			this._abortController = null;
		}
	}

	updateBackToTopVisibility() {
		if (!this.backToTop) return;
		const threshold = 200;
		const visible = this.currentPage >= 2 && window.scrollY > threshold;
		if (visible) {
			this.backToTop.style.display = 'flex';
		} else {
			this.backToTop.style.display = 'none';
		}
		// Keep the progress pill visibility in sync (P7-B2 follow-up).
		if (typeof this.updateProgressPill === 'function') {
			this.updateProgressPill();
		}
	}
}

// Subcollection Carousel
class AtelierSubcollectionCarousel {
	constructor() {
		this.wrapper = document.querySelector('.atelier-carousel-wrapper');
		this.track = document.querySelector('.atelier-carousel-track');
		this.prevBtn = document.querySelector('.atelier-carousel-prev');
		this.nextBtn = document.querySelector('.atelier-carousel-next');
		this.slides = document.querySelectorAll('.atelier-carousel-slide');
		this.slideWidth = 160; // measured in init()
		this._resizeRaf = null;
		this._scrollRaf = null;
		this._saveTimer = null;
		
	}

	init() {
		if (this.wrapper && this.track && this.prevBtn && this.nextBtn) {
			this.measure();
			this.prevBtn.addEventListener('click', () => this.scrollPrev());
			this.nextBtn.addEventListener('click', () => this.scrollNext());
			this.wrapper.addEventListener('scroll', () => this.onScroll(), { passive: true });
			// Restore scroll position (per-page)
			try {
				const key = this.getScrollKey();
				const saved = sessionStorage.getItem(key);
				if (saved) this.wrapper.scrollLeft = parseFloat(saved) || 0;
			} catch (_) {}
			this.updateButtonStates();
			window.addEventListener('resize', () => this.onResize(), { passive: true });
		}
	}

	getScrollKey() {
		const path = (window.location && window.location.pathname) ? window.location.pathname : 'atelier';
		return `atelier_carousel_scroll:${path}`;
	}

	onResize() {
		if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
		this._resizeRaf = requestAnimationFrame(() => {
			this.measure();
			this.updateButtonStates();
		});
	}

	measure() {
		// Measure slide width + CSS gap so mobile size/gap changes don't break navigation.
		const first = this.slides && this.slides[0] ? this.slides[0] : null;
		const slideW = first ? first.getBoundingClientRect().width : 159;
		const gapStr = this.track ? (getComputedStyle(this.track).gap || '0') : '0';
		const gap = parseFloat(String(gapStr).split(' ')[0]) || 0;
		this.slideWidth = slideW + gap;
	}

	onScroll() {
		if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
		this._scrollRaf = requestAnimationFrame(() => {
			this.updateButtonStates();
			// Persist scroll position (debounced)
			try {
				if (this._saveTimer) clearTimeout(this._saveTimer);
				this._saveTimer = setTimeout(() => {
					sessionStorage.setItem(this.getScrollKey(), String(this.wrapper?.scrollLeft || 0));
				}, 120);
			} catch (_) {}
		});
	}

	scrollPrev() {
		if (!this.wrapper) return;
		this.wrapper.scrollBy({ left: -this.slideWidth, behavior: 'smooth' });
	}

	scrollNext() {
		if (!this.wrapper) return;
		this.wrapper.scrollBy({ left: this.slideWidth, behavior: 'smooth' });
	}

	updateButtonStates() {
		if (!this.wrapper) return;
		const maxScroll = Math.max(0, this.wrapper.scrollWidth - this.wrapper.clientWidth);
		const x = this.wrapper.scrollLeft || 0;

		const hasOverflow = maxScroll > 2;
		if (this.prevBtn) this.prevBtn.classList.toggle('is-hidden', !hasOverflow);
		if (this.nextBtn) this.nextBtn.classList.toggle('is-hidden', !hasOverflow);
		if (!hasOverflow) return;

		if (this.prevBtn) {
			this.prevBtn.disabled = x <= 2;
		}
		if (this.nextBtn) {
			this.nextBtn.disabled = x >= (maxScroll - 2);
		}
	}
}

// Filter Bar and Sidebar
class AtelierFilterBar {
	constructor() {
		// Initialize elements
		this.filterBar = document.querySelector('.atelier-filter-bar');
		this.filterToggle = document.getElementById('atelier-filter-toggle');
		this.filterSidebar = document.getElementById('atelier-filter-sidebar');
		this.filterOverlay = document.getElementById('atelier-filter-overlay');
		this.filterClose = document.getElementById('atelier-filter-close');
		this.filterApply = document.getElementById('atelier-filter-apply');
		this.filterClear = document.getElementById('atelier-filter-clear');
		this.barClear = document.getElementById('atelier-bar-clear');
		this.sortToggle = document.getElementById('atelier-sort-toggle');
		this.sortDropdown = document.getElementById('atelier-sort-dropdown');
		this.sortOptions = document.querySelectorAll('.atelier-sort-option');
		this.filterOptionsContainer = document.querySelector('.atelier-filter-options');
		this.modelToggle = document.getElementById('atelier-model-toggle');
		// a11y trap state (safe defaults)
		this._focusTrapBound = false;
		this._lastOpener = null;
		this._focusGuards = { first: null, last: null };
		this._onDocKeydown = (e) => this._handleDocumentKeydown(e);
		this._onDocClick = (e) => {
			if (!this.isOpen) return;
			if (this.filterSidebar && !this.filterSidebar.contains(e.target) && !this.filterToggle?.contains(e.target)) {
				// click outside closes sidebar
				this.closeSidebar();
			}
		};
		
		// Initialize state
		this.isOpen = false;
		this.isSortOpen = false;
		this.currentSort = '';
		this.activePreset = null; // Track which preset is active
		this.accordionInitialized = false; // Flag to prevent multiple initializations
		this.originalTotalCount = 0; // Store original total count
		this.applyCountTimer = null; // debounce timer for apply count preview (mobile)
		this._popstateBound = false;
		this._filterAbortController = null;
		
		// Initialize
		this.init();
	}

	abortPending() {
		try {
			this._filterAbortController?.abort();
		} catch (_) {}
		this._filterAbortController = null;
	}

	init() {
		this.bindEvents();
		this.initSortState();
		this.initFilterState();
		this.initImageType();
		this.initPresets();
		
		// Store original total count.
		// Phase 7 / P7-C8: prefer `data-original-total-results` which is
		// rendered server-side via `collection.all_products_count` and
		// always carries the FULL unfiltered collection count, even on a
		// refresh of a filtered URL. Fall back to `data-total-results`
		// (the filtered count) only if the new attribute is missing — e.g.
		// on the 28-card variant or any future grid section that hasn't
		// been updated yet.
		const grid = document.querySelector('.atelier-grid');
		const originalRaw = grid?.dataset.originalTotalResults || grid?.dataset.totalResults || 0;
		this.originalTotalCount = parseInt(originalRaw, 10) || 0;
		this._currentFilteredCount = parseInt(grid?.dataset.totalResults || 0, 10) || 0;
		
		// Subcollection buttons are server-rendered; just clamp to fit.
		this.checkButtonFit();
		window.addEventListener('resize', () => this.checkButtonFit(), { passive: true });
		
		// Initial filter count update
		this.updateFilterCount();
		this.updateFilterResultsCount(this._currentFilteredCount);
		// Phase 7 / P7-C9: Apply label seeded with the CURRENT (filtered or
		// not) count from the server render so opening the drawer on a
		// refreshed-filtered URL shows "VIEW (39) ITEMS" immediately —
		// without waiting for a redundant fetch. Clear button is enabled
		// iff any filter checkboxes/presets are pre-selected from URL state.
		this.updateApplyButtonLabel(this._currentFilteredCount || this.originalTotalCount);
		this.updateClearButtonState();

		// High-end: keep grid + UI in sync with Back/Forward
		this.bindPopstate();
	}

	bindPopstate() {
		if (this._popstateBound) return;
		this._popstateBound = true;
		window.addEventListener('popstate', () => this.onPopState());
	}

	onPopState() {
		// Sync UI state from the URL, then render grid for the URL without pushing history.
		this.syncFilterUiFromUrl();
		this.initSortState();
		this.applyUrl(window.location.href, { pushState: false, fromPopstate: true });
	}

	syncFilterUiFromUrl() {
		if (!this.filterSidebar) return;

		const params = new URLSearchParams(window.location.search);

		// Reset checkboxes
		this.filterSidebar.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
			cb.checked = false;
		});

		// Restore checked state for all named facet checkboxes (Shopify native filters)
		const named = this.filterSidebar.querySelectorAll('input[type="checkbox"][name]');
		named.forEach((cb) => {
			const name = cb.getAttribute('name');
			const value = cb.value;
			if (!name) return;
			const values = params.getAll(name);
			cb.checked = values.includes(value);
		});

		// Restore price presets (custom), based on the param names configured in the sidebar.
		const minParam = this.filterSidebar.dataset.priceMinParam || 'filter.v.price.gte';
		const maxParam = this.filterSidebar.dataset.priceMaxParam || 'filter.v.price.lte';
		const mins = params.getAll(minParam);
		const maxs = params.getAll(maxParam);

		this.filterSidebar.querySelectorAll('.price-preset').forEach((cb) => {
			const min = cb.dataset.min ?? '';
			const max = cb.dataset.max ?? '';
			// Match pairs; allow open-ended max ("") presets
			const matchMin = min === '' ? mins.length === 0 : mins.includes(String(min));
			const matchMax = max === '' ? true : maxs.includes(String(max));
			cb.checked = matchMin && matchMax;
		});

		// Restore legacy slider (if present)
		const priceFilter = params.get('filter.price');
		if (priceFilter) {
			const priceRange = this.filterSidebar.querySelector('.atelier-price-range');
			if (priceRange) priceRange.value = priceFilter;
		}

		this.updateFilterOptionStates();
		this.updateFilterCount();
		// Phase 7 / P7-C9: keep Clear button state in sync after Back/Forward.
		this.updateClearButtonState();
	}

	checkButtonFit() {
		const filterOptions = this.filterOptionsContainer;
		const buttons = filterOptions?.querySelectorAll('.atelier-filter-option');
		
		if (!filterOptions || !buttons.length) return;
		
		// Reset all buttons to visible
		buttons.forEach(button => {
			button.style.display = 'flex';
		});
		
		// Check if buttons overflow the container
		const containerWidth = filterOptions.offsetWidth;
		const containerScrollWidth = filterOptions.scrollWidth;
		
		if (containerScrollWidth > containerWidth) {
			// Hide buttons from the end until they fit
			for (let i = buttons.length - 1; i >= 0; i--) {
				buttons[i].style.display = 'none';
				
				// Re-check if they fit now
				if (filterOptions.scrollWidth <= containerWidth) {
					break;
				}
			}
		}
	}

	initImageType() {
		// Get saved image type preference
		const savedImageType = localStorage.getItem('atelier_image_type') || 'product';
		
		// Apply the saved preference
		this.switchProductImages(savedImageType);
	}

	initFilterState() {
		// Get current filter parameters from URL
		const urlParams = new URLSearchParams(window.location.search);
		
		// Initialize material filters
		const materialFilters = urlParams.getAll('filter.material');
		materialFilters.forEach(material => {
			const safeMaterial = material.replace(/"/g, '\\"');
			const checkbox = this.filterSidebar?.querySelector(`input[value="${safeMaterial}"]`);
			if (checkbox) {
				checkbox.checked = true;
			}
		});
		
		// Initialize stone filters
		const stoneFilters = urlParams.getAll('filter.stone');
		stoneFilters.forEach(stone => {
			const safeStone = stone.replace(/"/g, '\\"');
			const checkbox = this.filterSidebar?.querySelector(`input[value="${safeStone}"]`);
			if (checkbox) {
				checkbox.checked = true;
			}
		});
		
		// Initialize price filter
		const priceFilter = urlParams.get('filter.price');
		if (priceFilter) {
			const priceRange = this.filterSidebar?.querySelector('.atelier-price-range');
			if (priceRange) {
				priceRange.value = priceFilter;
			}
		}
		
		// Update filter option states
		this.updateFilterOptionStates();
	}

	updateFilterOptionStates() {
		// Check if any filters are active
		const hasActiveFilters = this.filterSidebar?.querySelector('input[type="checkbox"]:checked') || 
								this.filterSidebar?.querySelector('.atelier-price-range')?.value !== this.filterSidebar?.querySelector('.atelier-price-range')?.max;
		
		// Update filter options visual state
		this.filterOptionsContainer?.querySelectorAll('.atelier-filter-option').forEach(option => {
			if (hasActiveFilters) {
				option.classList.add('has-filters');
			} else {
				option.classList.remove('has-filters');
			}
		});
	}

	syncSortOptions(value) {
		let labelText = '';
		this.sortOptions.forEach((opt) => {
			const match = opt.dataset.value === value;
			opt.classList.toggle('active', match);
			const radio = opt.querySelector?.('input[type="radio"]');
			if (radio) radio.checked = match;
			if (match && !labelText) {
				const named = opt.querySelector?.('.atelier-filter-label');
				labelText = (named?.textContent || opt.textContent || '').trim();
			}
		});
		return labelText;
	}

	initSortState() {
		// Get current sort from URL
		const urlParams = new URLSearchParams(window.location.search);
		const currentSort = urlParams.get('sort_by') || 'manual';
		const label = this.syncSortOptions(currentSort);

		this.setSortToggleLabel(label);

		this.currentSort = currentSort;
	}

	bindEvents() {
		// Filter toggle
		this.filterToggle?.addEventListener('click', () => this.openSidebar());
		this.filterClose?.addEventListener('click', () => this.closeSidebar());
		this.filterOverlay?.addEventListener('click', () => this.closeSidebar());
		
		// Filter actions.
		// Phase 7 / P7-C9 LIVE FILTER MODE:
		//  - Apply button is now a CLOSE-DRAWER action. Filters were
		//    already applied live as the user toggled them, so this click
		//    just closes the drawer. Cancels any pending debounced fetch
		//    on the way out so we don't race a half-typed selection.
		//  - Clear button: clears all selections, applies the cleared
		//    state live (resets URL to unfiltered), closes the drawer.
		this.filterApply?.addEventListener('click', () => {
			if (this._applyFiltersTimer) {
				clearTimeout(this._applyFiltersTimer);
				this._applyFiltersTimer = null;
				// Flush a final apply so any uncommitted toggle lands.
				this.applyFilters();
			}
			this.closeSidebar();
		});
		this.filterClear?.addEventListener('click', () => {
			this.clearFilters();
			this.closeSidebar();
		});
		this.barClear?.addEventListener('click', () => {
			this.clearFilters();
		});
		
		// Sort toggle
		this.sortToggle?.addEventListener('click', (e) => {
			e.stopPropagation();
			this.toggleSort();
		});

		// Delegated fallback: ensures sort works after hot-reload/DOM replacements
		if (!window.__atelierSortDelegatedBound) {
			document.addEventListener('click', (e) => {
				const sortBtn = e.target.closest && e.target.closest('#atelier-sort-toggle');
				if (!sortBtn) return;
				// Prevent outside-click closer from immediately closing
				e.preventDefault();
				e.stopPropagation();
				const dropdown = document.getElementById('atelier-sort-dropdown');
				if (dropdown) {
					// Toggle visual state; close handler will manage class/state on outside clicks
					dropdown.classList.toggle('open');
				}
			}, true);
			window.__atelierSortDelegatedBound = true;
		}
		
		// Sort options
		this.sortOptions.forEach(option => {
			option.addEventListener('click', () => this.selectSort(option));
		});
		
		// Model toggle switch
		this.modelToggle?.addEventListener('change', (e) => {
			this.switchProductImages(e.target.checked ? 'model' : 'product');
		});
		
		// Scroll detection for sticky behavior
		window.addEventListener('scroll', () => this.handleScroll());
		
		// Resize detection for button fitting
		window.addEventListener('resize', () => this.checkButtonFit());
		
		// Close dropdown when clicking outside
		document.addEventListener('click', (e) => {
			if (!this.sortToggle?.contains(e.target) && !this.sortDropdown?.contains(e.target)) {
				this.closeSort();
			}
		});
		
		// Keyboard events
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				if (this.isOpen) {
					this.closeSidebar();
				}
				if (this.isSortOpen) {
					this.closeSort();
				}
			}
		});

		// Phase 7 / P7-C9: filter changes now trigger LIVE apply (debounced).
		// updateFilterCount: refreshes the "All Filters (N)" badge text.
		// updateClearButtonState: enables/disables Clear based on whether
		//   anything is selected.
		// applyFiltersDebounced: 300ms debounce → AJAX fetch via Section
		//   Rendering API → grid + Apply button label update + scroll-to-top.
		document.addEventListener('change', (e) => {
			if (e.target.matches('.atelier-filter-checkbox input, .atelier-price-range, .price-preset')) {
				this.syncGroupedFilterInputs(e.target);
				this.updateFilterCount();
				this.updateClearButtonState();
				this.applyFiltersDebounced();
			}
		});

		// Add accordion functionality
		this.initAccordion();
	}

	// NEW: Accordion functionality
	initAccordion() {
		// Prevent multiple initializations
		if (this.accordionInitialized) {
			return;
		}

		// Check if elements exist
		const toggleButtons = document.querySelectorAll('.atelier-filter-group-toggle');
		
		if (toggleButtons.length === 0) {
			return;
		}
		
		// Use event delegation for better reliability
		document.addEventListener('click', (e) => {
			if (e.target.closest('.atelier-filter-group-toggle')) {
				e.preventDefault();
				
				const button = e.target.closest('.atelier-filter-group-toggle');
				const targetId = button.dataset.target;
				const content = document.getElementById(targetId);
				
				if (content) {
					const isOpen = content.classList.contains('open');
					
					if (isOpen) {
						content.classList.remove('open');
						button.classList.remove('active');
					} else {
						content.classList.add('open');
						button.classList.add('active');
					}
				}
			}
		});
		
		this.accordionInitialized = true;
	}

	handleScroll() {
		const scrollThreshold = 200; // Adjust this value as needed
		
		if (this.filterBar) {
			if (window.scrollY > scrollThreshold) {
				this.filterBar.classList.add('is-sticky');
			} else {
				this.filterBar.classList.remove('is-sticky');
			}
		}
	}

	initFilterOptions() {
		this.filterOptionsContainer?.querySelectorAll('.atelier-filter-option').forEach(option => {
			option.addEventListener('click', () => {
				this.toggleFilterOption(option);
			});
		});
	}

	ensureSidebarPortal() {
		let didPortal = false;
		if (this.filterSidebar && !this.filterSidebar.__portaledToBody) {
			document.body.appendChild(this.filterSidebar);
			this.filterSidebar.__portaledToBody = true;
			didPortal = true;
		}
		if (this.filterOverlay && !this.filterOverlay.__portaledToBody) {
			document.body.appendChild(this.filterOverlay);
			this.filterOverlay.__portaledToBody = true;
			didPortal = true;
		}
		return didPortal;
	}

	openSidebar() {
		if (this.isOpen) return;
		const didPortal = this.ensureSidebarPortal();
		this.isOpen = true;
		this._lastOpener = document.activeElement;
		// On first open after portaling, force a layout pass so CSS transition can animate.
		if (didPortal) {
			this.filterSidebar?.getBoundingClientRect();
		}
		this.filterSidebar?.classList.add('open');
		this.filterOverlay?.classList.add('open');
		$s.hideScroll();
		// minimal aria
		if (this.filterSidebar) {
			this.filterSidebar.setAttribute('tabindex', '-1');
		}
		// add focus guards (sentinels) for Safari
		if (this.filterSidebar && !this._focusGuards.first) {
			this._focusGuards.first = document.createElement('button');
			this._focusGuards.first.className = 'visually-hidden';
			this._focusGuards.first.tabIndex = 0;
			this._focusGuards.first.setAttribute('aria-hidden', 'true');
			this._focusGuards.first.addEventListener('focus', () => {
				const focusables = $s.getFocusableElements(this.filterSidebar);
				const last = focusables[focusables.length - 1];
				(last || this.filterSidebar).focus();
			});
			this._focusGuards.last = document.createElement('button');
			this._focusGuards.last.className = 'visually-hidden';
			this._focusGuards.last.tabIndex = 0;
			this._focusGuards.last.setAttribute('aria-hidden', 'true');
			this._focusGuards.last.addEventListener('focus', () => {
				const focusables = $s.getFocusableElements(this.filterSidebar);
				const first = focusables[0];
				(first || this.filterSidebar).focus();
			});
			this.filterSidebar.insertBefore(this._focusGuards.first, this.filterSidebar.firstChild);
			this.filterSidebar.appendChild(this._focusGuards.last);
		}
		// attach lightweight trap on container only
		if (this.filterSidebar && !this._focusTrapBound) {
			this.filterSidebar.addEventListener('keydown', this._trapKeydown = (e) => {
				if (e.key === 'Escape' || e.key === 'Esc') {
					e.preventDefault();
					this.closeSidebar();
					return;
				}
				if (e.key !== 'Tab') return;
				const focusables = $s.getFocusableElements(this.filterSidebar);
				if (!focusables.length) return;
				const first = focusables[0];
				const last = focusables[focusables.length - 1];
				if (e.shiftKey && document.activeElement === first) {
					e.preventDefault();
					last.focus();
				} else if (!e.shiftKey && document.activeElement === last) {
					e.preventDefault();
					first.focus();
				}
			});
			this._focusTrapBound = true;
		}
		// ensure Escape works even if focus leaves container (Safari)
		document.addEventListener('keydown', this._onDocKeydown, true);
		document.addEventListener('click', this._onDocClick, true);
		// focus first element shortly after open
		setTimeout(() => {
			const focusables = $s.getFocusableElements(this.filterSidebar);
			(focusables[0] || this.filterSidebar)?.focus({ preventScroll: true });
		}, 0);
		
		// Initialize presets when sidebar opens (fallback)
		setTimeout(() => {
			this.initPresets();
			this.initAccordion(); // Ensure accordion is initialized
			this.updateApplyButtonLabel(this.originalTotalCount);
		}, 100);
	}

	closeSidebar() {
		if (!this.isOpen) return;
		this.isOpen = false;
		this.filterSidebar?.classList.remove('open');
		this.filterOverlay?.classList.remove('open');
		$s.showScroll();
		// detach trap
		if (this.filterSidebar && this._trapKeydown) {
			this.filterSidebar.removeEventListener('keydown', this._trapKeydown);
			this._trapKeydown = null;
			this._focusTrapBound = false;
		}
		// remove guards
		if (this._focusGuards.first && this._focusGuards.first.parentNode) {
			this._focusGuards.first.parentNode.removeChild(this._focusGuards.first);
			this._focusGuards.first = null;
		}
		if (this._focusGuards.last && this._focusGuards.last.parentNode) {
			this._focusGuards.last.parentNode.removeChild(this._focusGuards.last);
			this._focusGuards.last = null;
		}
		document.removeEventListener('keydown', this._onDocKeydown, true);
		document.removeEventListener('click', this._onDocClick, true);
		// restore focus
		if (this._lastOpener && typeof this._lastOpener.focus === 'function') {
			this._lastOpener.focus({ preventScroll: true });
		}
	}

	toggleFilterOption(option) {
		// Remove active from all options
		this.filterOptionsContainer?.querySelectorAll('.atelier-filter-option').forEach(opt => opt.classList.remove('active'));
		// Add active to clicked option
		option.classList.add('active');
		
		// Navigate to the subcollection
		const url = option.dataset.url;
		if (url) {
			window.location.href = url;
		}
	}

	scrollToFilterGroup(filterType) {
		const filterGroup = this.filterSidebar?.querySelector(`[data-filter-group="${filterType}"]`);
		if (filterGroup) {
			filterGroup.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}

	// Phase 7 / P7-C9: applyFilters now ALWAYS runs in live mode (drawer
	// stays open, grid loading dim suppressed). The Apply button click is
	// just a close-drawer action; it does not call this method anymore.
	// Triggered by filter change events (debounced) and by clearFilters().
	applyFilters() {
		const filters = this.getSelectedFilters();
		const url = this.buildFilterUrl(filters);
		this.applyUrl(url, {
			pushState: true,
			fromPopstate: false,
			keepSidebarOpen: true,
			suppressGridLoading: true,
		});
	}

	// Phase 7 / P7-C9: debounce wrapper for live filter toggles. 300ms
	// gives users time to click multiple checkboxes before a fetch fires;
	// each new toggle resets the timer + the in-flight fetch (via
	// abortPending inside applyUrl).
	applyFiltersDebounced() {
		if (this._applyFiltersTimer) clearTimeout(this._applyFiltersTimer);
		this._applyFiltersTimer = setTimeout(() => {
			this._applyFiltersTimer = null;
			this.applyFilters();
		}, 300);
	}

	// Phase 7 / P7-C9 live filter mode adds two options:
	//  - keepSidebarOpen: don't auto-close the mobile sidebar after Apply
	//    (we do live-on-toggle from inside the open drawer).
	//  - suppressGridLoading: skip the dim+disable grid loading overlay
	//    (the Apply button's own inline spinner is enough feedback when
	//    the user is focused on the open drawer).
	applyUrl(url, { pushState = true, fromPopstate = false, keepSidebarOpen = false, suppressGridLoading = false } = {}) {
		// Cancel any in-flight filter request
		this.abortPending();
		this._filterAbortController = new AbortController();

		// Also cancel any in-flight infinite scroll request (prevents mixed state)
		if (window.atelierInfiniteScroll && typeof window.atelierInfiniteScroll.abortPending === 'function') {
			window.atelierInfiniteScroll.abortPending();
		}

		if (!suppressGridLoading) {
			this.showFilterLoading();
		}
		// Apply button gets its own loading state for live mode so the user
		// always sees something happening as soon as they toggle a filter,
		// even before the 500ms grid-loading timer would have fired.
		if (keepSidebarOpen) {
			this.setApplyLoading(true);
		}

		// Fetch only the grid markup (Section Rendering API) to avoid downloading/parsing the full page.
		const sectionUrl = (() => {
			try {
				const u = new URL(url);
				u.searchParams.set('section_id', getAtelierGridSectionId());
				return u.toString();
			} catch (_) {
				// Fallback: best effort (shouldn't happen because buildFilterUrl returns absolute URL)
				return url;
			}
		})();

		const cacheKey = __atelierCache.key('filters', sectionUrl);
		const cached = __atelierCache.get(cacheKey);

		const handlePayload = (payload) => {
			// Update grid from cached payload (avoids full DOM parsing)
			const currentGrid = document.querySelector('.atelier-grid');
			const totalResultsNum = parseInt(payload?.totalResults || '0', 10) || 0;

			if (payload && currentGrid) {
				// Clear prior empties
				document.querySelectorAll('.atelier-empty').forEach(n => n.remove());

				if (totalResultsNum <= 0) {
					// Empty state: hide grid and show a single message
					const empty = document.createElement('div');
					empty.className = 'atelier-empty';
					empty.innerHTML = '<p>No products found.</p>';
					const endMsg = document.querySelector('.atelier-end-message');
					if (endMsg && endMsg.parentNode) {
						endMsg.parentNode.insertBefore(empty, endMsg);
						const p = endMsg.querySelector('p');
						if (p) p.textContent = "You've viewed all 0 products in this collection";
						endMsg.style.display = 'block';
					}
					currentGrid.style.display = 'none';
					const loadMore = document.querySelector('.atelier-load-more');
					if (loadMore) loadMore.style.display = 'none';
				} else {
					currentGrid.innerHTML = payload.gridInner || '';
					if (payload.totalPages) currentGrid.dataset.totalPages = payload.totalPages;
					if (payload.totalResults) currentGrid.dataset.totalResults = payload.totalResults;
					if (payload.currentPage) currentGrid.dataset.currentPage = payload.currentPage;
					currentGrid.style.display = '';
					const endMsgNode = document.querySelector('.atelier-end-message');
					if (endMsgNode) endMsgNode.style.display = 'none';
					const loadMore = document.querySelector('.atelier-load-more');
					if (loadMore) loadMore.style.display = '';
				}

				// Phase 7 / P7-C7: do NOT overwrite the H1 .results-count with the
				// filtered count. It stays at the server-rendered unfiltered total
				// (collection.all_products_count). The chip + floating pill carry
				// the filtered-context numbers.
			}

			// Announce update for a11y
			const announce = document.getElementById('atelier-announce');
			if (announce) {
				announce.textContent = totalResultsNum > 0 ? `Updated: ${totalResultsNum} results` : 'No products found';
			}

			// Update URL without page reload (unless we are responding to Back/Forward)
			if (pushState) window.history.pushState({}, '', url);

			// Reset infinite scroll.
			// Phase 7 / P7-C2 follow-up: `resetInfiniteScroll` is a method on
			// AtelierFilterBar (this), NOT on the InfiniteScroll instance.
			// The previous call `window.atelierInfiniteScroll.resetInfiniteScroll()`
			// threw `TypeError: ... is not a function` on every filter Apply,
			// killing every line after it (chip update, hideFilterLoading, mobile
			// sidebar close, Apply-button label refresh). The internal guard on
			// `window.atelierInfiniteScroll` lives inside the method, so we don't
			// need an outer check here.
			this.resetInfiniteScroll();

			// Update filter results count pill
			this.updateFilterResultsCount(totalResultsNum, this.originalTotalCount);

			this.hideFilterLoading();
			this.setApplyLoading(false);

			// Mobile auto-close (one-shot Apply path) OR live-mode scroll-to-top.
			// In live mode the drawer stays open; we only scroll the grid back
			// to the top so the user always sees the first matching products
			// instead of staying parked deep in the previous filter's results.
			if (!fromPopstate && !keepSidebarOpen && this.isMobile()) {
				this.closeSidebar();
				const grid = document.querySelector('.atelier-grid');
				if (grid && typeof grid.scrollIntoView === 'function') grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
				else window.scrollTo({ top: 0, behavior: 'smooth' });
			} else if (keepSidebarOpen && !fromPopstate) {
				// Live mode (P7-C9): scroll grid to top on each filter change.
				const grid = document.querySelector('.atelier-grid');
				if (grid && typeof grid.scrollIntoView === 'function') {
					grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
				} else {
					window.scrollTo({ top: 0, behavior: 'smooth' });
				}
			}

			// Update filter count after applying.
			// Phase 7 / P7-C9: Apply button is no longer "disabled until
			// dirty" — it now serves as the close-drawer action and always
			// reflects the live filtered count. Clear button is the one
			// that gets disabled-when-empty.
			this.updateFilterCount();
			this.updateClearButtonState();
			this.updateApplyButtonLabel(totalResultsNum);
		};

		if (cached) {
			handlePayload(cached);
			this._filterAbortController = null;
			return;
		}

		fetch(sectionUrl, { signal: this._filterAbortController.signal })
			.then(response => response.text())
			.then(html => {
				const doc = new DOMParser().parseFromString(html, 'text/html');
				const newGrid = doc.querySelector('.atelier-grid');

				const payload = {
					gridInner: newGrid ? newGrid.innerHTML : '',
					totalPages: newGrid?.dataset.totalPages || '',
					totalResults: newGrid?.dataset.totalResults || '0',
					currentPage: newGrid?.dataset.currentPage || '1'
				};

				__atelierCache.set(cacheKey, payload);
				handlePayload(payload);
			})
			.catch(error => {
				if (error && (error.name === 'AbortError' || String(error).includes('AbortError'))) {
					// Swallow aborts (a newer request took over)
					return;
				}
				console.error('Error applying filters:', error);
				this.hideFilterLoading();
				this.setApplyLoading(false);
			})
			.finally(() => {
				// Only clear if this request is still current
				this._filterAbortController = null;
			});
	}

buildFilterUrl(filters) {
    const url = new URL(window.location.href);
    // Remove existing storefront filter params
    Array.from(url.searchParams.keys()).forEach(k => {
        if (k.startsWith('filter.')) url.searchParams.delete(k);
    });
    // Reset pagination
    url.searchParams.delete('page');
    // Append selected facet pairs
    if (filters.pairs && filters.pairs.length) {
        filters.pairs.forEach(p => url.searchParams.append(p.name, p.value));
    }
    // Price presets (multiple). Prefer official param names if available
    if (filters.priceRanges && filters.priceRanges.length) {
        const sidebar = document.getElementById('atelier-filter-sidebar');
        const minParam = sidebar?.dataset.priceMinParam || 'filter.v.price.gte';
        const maxParam = sidebar?.dataset.priceMaxParam || 'filter.v.price.lte';
        filters.priceRanges.forEach(r => {
            if (r.min != null) url.searchParams.append(minParam, String(r.min));
            if (r.max != null) url.searchParams.append(maxParam, String(r.max));
        });
    }
    return url.toString();
}

	// Phase 7 / P7-C4: defer the loading state for ~500ms so fast responses
	// (cached or quick network) never flash a dim+disabled grid. If the
	// response returns first, hideFilterLoading() cancels the pending timer
	// and the user sees zero visual disruption. Slower responses still get
	// the dim feedback so users aren't left wondering if anything is happening.
	showFilterLoading() {
		if (this._loadingTimer) return;
		this._loadingTimer = setTimeout(() => {
			this._loadingTimer = null;
			this.filterBar?.classList.add('loading');
			const grid = document.querySelector('.atelier-grid');
			if (grid) {
				grid.style.opacity = '0.5';
				grid.style.pointerEvents = 'none';
			}
		}, 500);
	}

	hideFilterLoading() {
		if (this._loadingTimer) {
			clearTimeout(this._loadingTimer);
			this._loadingTimer = null;
		}
		this.filterBar?.classList.remove('loading');
		const grid = document.querySelector('.atelier-grid');
		if (grid) {
			grid.style.opacity = '';
			grid.style.pointerEvents = '';
		}
	}

	resetInfiniteScroll() {
		// Reset infinite scroll state
		const infiniteScroll = window.atelierInfiniteScroll;
		if (infiniteScroll) {
			infiniteScroll.grid = document.querySelector('.atelier-grid');
			infiniteScroll.currentPage = parseInt(infiniteScroll.grid?.dataset.currentPage || '1', 10) || 1;
			infiniteScroll.totalPages = parseInt(document.querySelector('.atelier-grid').dataset.totalPages) || 1;
			infiniteScroll.totalResults = parseInt(document.querySelector('.atelier-grid').dataset.totalResults || 0);
			infiniteScroll.isLoading = false;
			
			// Re-initialize observer
			if (infiniteScroll.observer) {
				infiniteScroll.observer.disconnect();
			}
			infiniteScroll.init();
		}
	}

	clearFilters() {
		// Clear active preset state
		this.clearActivePreset();
		
		// Clear checkboxes
		const checkboxes = document.querySelectorAll('.atelier-filter-checkbox input');
		checkboxes.forEach(checkbox => {
			checkbox.checked = false;
		});
		
		// Reset price range
		const priceRange = document.querySelector('.atelier-price-range');
		if (priceRange) {
			priceRange.value = priceRange.max;
		}
		
		// Remove active states from filter options
		this.filterOptionsContainer?.querySelectorAll('.atelier-filter-option').forEach(option => option.classList.remove('active'));
		
		// Update filter count
		this.updateFilterCount();
		
		// Reset filter results count
		this.updateFilterResultsCount(0, 0);
		
		// Apply the cleared filters (this will update the page).
		// Phase 7 / P7-C9: applyFilters now runs in live mode (drawer
		// stays open, no grid dim). The Clear click handler closes the
		// drawer separately. updateClearButtonState disables the Clear
		// button now that nothing is selected.
		this.applyFilters();
		this.updateClearButtonState();
	}

	syncGroupedFilterInputs(input) {
		if (!input || input.type !== 'checkbox') return;
		const row = input.closest('.atelier-filter-checkbox');
		if (!row) return;
		const boxes = row.querySelectorAll('input[type="checkbox"][name]');
		if (boxes.length < 2) return;
		boxes.forEach((checkbox) => {
			if (checkbox !== input) checkbox.checked = input.checked;
		});
	}

getSelectedFilters() {
    const filters = { pairs: [], price: null, priceRanges: [] };
    // Native facet inputs have a name (param_name). Collect all checked.
    const inputs = this.filterSidebar?.querySelectorAll('input[type="checkbox"][name]:checked');
    inputs?.forEach(input => {
        filters.pairs.push({ name: input.name, value: input.value });
    });
    // Price (dollars)
    const priceRange = this.filterSidebar?.querySelector('.atelier-price-range');
    if (priceRange) {
        filters.price = parseInt(priceRange.value);
    }
    // Price presets (dollars)
    const presets = this.filterSidebar?.querySelectorAll('.price-preset:checked');
    presets?.forEach(p => {
        // data-min/max are in dollars here; use as-is (your store expects dollars)
        const minD = p.dataset.min !== '' && p.dataset.min != null ? parseInt(p.dataset.min, 10) : null;
        const maxD = p.dataset.max !== '' && p.dataset.max != null ? parseInt(p.dataset.max, 10) : null;
        const min = (minD != null) ? minD : null;
        const max = (maxD != null) ? maxD : null;
        filters.priceRanges.push({ min, max });
    });
    return filters;
}

	toggleSort() {
		if (this.isSortOpen) {
			this.closeSort();
		} else {
			this.openSort();
		}
	}

	openSort() {
		this.isSortOpen = true;
		if (!this.sortDropdown) return;
		// default open
		this.sortDropdown.classList.add('open');
		// viewport clamp / flip
		const btnRect = this.sortToggle?.getBoundingClientRect();
		const ddRect = this.sortDropdown.getBoundingClientRect();
		if (btnRect && ddRect) {
			const spaceBelow = window.innerHeight - btnRect.bottom;
			const needed = Math.min(ddRect.height, Math.min(window.innerHeight * 0.6, 360));
			if (spaceBelow < needed) {
				// not enough room below → dropup
				this.sortDropdown.style.top = 'auto';
				this.sortDropdown.style.bottom = '100%';
				this.sortDropdown.style.transform = 'translateY(10px)';
			} else {
				// reset to dropdown
				this.sortDropdown.style.bottom = '';
				this.sortDropdown.style.top = '100%';
				this.sortDropdown.style.transform = '';
			}
		}
	}

	setSortToggleLabel(label) {
		const sortText = this.sortToggle?.querySelector('.atelier-sort-text');
		if (sortText) sortText.textContent = label ? 'Sort by:' : 'Sort by';
		let current = this.sortToggle?.querySelector('.atelier-sort-current');
		if (!current && this.sortToggle) {
			current = document.createElement('span');
			current.className = 'atelier-sort-current';
			const icon = this.sortToggle.querySelector('.atelier-sort-icon');
			this.sortToggle.insertBefore(current, icon || null);
		}
		if (current) current.textContent = label || '';
	}

	closeSort() {
		this.isSortOpen = false;
		if (!this.sortDropdown) return;
		this.sortDropdown.classList.remove('open');
		this.sortDropdown.style.bottom = '';
		this.sortDropdown.style.top = '';
		this.sortDropdown.style.transform = '';
	}

	selectSort(option) {
		const value = option.dataset.value;
		const label = this.syncSortOptions(value);
		this.currentSort = value;

		this.setSortToggleLabel(label);

		// Close dropdown
		this.closeSort();
		
		// Apply sort
		this.applySort();
	}

	applySort() {
		// Build URL with sort parameter
		const currentUrl = new URL(window.location);
		currentUrl.searchParams.set('sort_by', this.currentSort);
		
		// Remove page parameter when sorting
		currentUrl.searchParams.delete('page');
		
		// Navigate to new URL
		window.location.href = currentUrl.toString();
	}

	switchProductImages(type) {
		// Update collection wrapper classes
		const collectionWrapper = document.querySelector('.atelier-collection');
		if (collectionWrapper) {
			if (type === 'model') {
				collectionWrapper.classList.remove('images-product');
				collectionWrapper.classList.add('images-model');
			} else {
				collectionWrapper.classList.remove('images-model');
				collectionWrapper.classList.add('images-product');
			}
		}
		
		// Update toggle state
		if (this.modelToggle) {
			this.modelToggle.checked = (type === 'model');
		}
		
		// Store preference in localStorage
		localStorage.setItem('atelier_image_type', type);
	}

	// NEW: Filter count functionality
	getActiveFilterCount() {
		let count = 0;
		document.querySelectorAll('.atelier-filter-sidebar .atelier-filter-checkbox:not(.atelier-filter-checkbox--radio)').forEach((row) => {
			if (row.querySelector('input:checked')) count++;
		});

		const priceRange = document.querySelector('.atelier-price-range');
		if (priceRange && priceRange.value < priceRange.max) {
			count++;
		}

		return count;
	}

	updateFilterCount() {
		const count = this.getActiveFilterCount();
		const filterText = this.filterToggle?.querySelector('.atelier-filter-text');
		if (filterText) filterText.textContent = 'All Filters';

		let num = this.filterToggle?.querySelector('.atelier-filter-n');
		if (!num && this.filterToggle && count > 0) {
			num = document.createElement('span');
			num.className = 'atelier-filter-n';
			if (filterText) filterText.after(num);
			else this.filterToggle.appendChild(num);
		}
		if (!num) return;

		if (count > 0) {
			num.textContent = String(count);
			num.hidden = false;
		} else {
			num.textContent = '';
			num.hidden = true;
		}
	}

	// Live result count next to All Filters. Uses the filtered product
	// count so Apply / Clear / live-filter all rewrite "N Results".
	updateFilterResultsCount(filteredCount) {
		const el = document.querySelector('.atelier-filter-results');
		if (!el) return;
		const count = Number.isFinite(filteredCount) ? filteredCount : 0;
		const num = el.querySelector('.atelier-filter-results-n');
		if (num) {
			num.textContent = String(count);
		} else {
			el.textContent = `(${count} Results)`;
		}
	}

	// Helpers
	isMobile() {
		return window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
	}

	// Phase 7 / P7-C9: Apply button now reads "VIEW (N) ITEMS" with the
	// live filtered count, on BOTH desktop and mobile (was mobile-only).
	// The number is wrapped in a span so we can preserve a child loading
	// spinner without rewriting the whole button content.
	updateApplyButtonLabel(count) {
		const btn = document.getElementById('atelier-filter-apply');
		if (!btn) return;
		const safe = Number.isFinite(count) ? count : 0;
		const spinner = btn.querySelector('.atelier-filter-apply-spinner');
		let label = btn.querySelector('.atelier-filter-apply-label');
		let num = btn.querySelector('.atelier-filter-apply-count');
		if (!label || !num) {
			btn.textContent = '';
			label = document.createElement('span');
			label.className = 'atelier-filter-apply-label';
			label.append('VIEW (');
			num = document.createElement('span');
			num.className = 'atelier-filter-apply-count';
			label.appendChild(num);
			label.append(') ITEMS');
			btn.appendChild(label);
		}
		num.textContent = String(safe);
		if (spinner) btn.appendChild(spinner);
	}

	// Phase 7 / P7-C9: toggle a tiny inline spinner inside the Apply
	// button while a live filter fetch is in flight, so the user always
	// sees something happening within ~300ms of toggling a checkbox.
	setApplyLoading(loading) {
		const btn = document.getElementById('atelier-filter-apply');
		if (!btn) return;
		let spinner = btn.querySelector('.atelier-filter-apply-spinner');
		if (loading) {
			if (!spinner) {
				spinner = document.createElement('span');
				spinner.className = 'atelier-filter-apply-spinner';
				spinner.setAttribute('aria-hidden', 'true');
				btn.appendChild(spinner);
			}
			btn.classList.add('is-loading');
		} else {
			btn.classList.remove('is-loading');
			if (spinner) spinner.remove();
		}
	}

	// Phase 7 / P7-C9: Clear is disabled when no filters are active so
	// shoppers don't get tricked into clicking a no-op control. Drives
	// off the same getActiveFilterCount() helper used for the "All
	// Filters (N)" badge.
	updateClearButtonState() {
		const hasActive = this.getActiveFilterCount() > 0;
		const clearBtn = document.getElementById('atelier-filter-clear');
		if (clearBtn) {
			clearBtn.disabled = !hasActive;
			clearBtn.classList.toggle('is-disabled', !hasActive);
		}
		if (this.barClear) this.barClear.hidden = !hasActive;
	}

	// NEW: Filter presets functionality
	initPresets() {
		const presetButtons = document.querySelectorAll('.atelier-preset-btn');
		
		// Check which presets are applicable to current collection
		this.checkPresetApplicability();
		
		presetButtons.forEach(button => {
			button.addEventListener('click', (e) => {
				e.preventDefault();
				const preset = button.dataset.preset;
				this.applyPreset(preset);
			});
		});
	}

	// NEW: Check if presets are applicable to current collection
	checkPresetApplicability() {
		const presetButtons = document.querySelectorAll('.atelier-preset-btn');
		
		// Get collection data
		const collectionData = this.getCollectionData();
		
		presetButtons.forEach(button => {
			const preset = button.dataset.preset;
			const isApplicable = this.isPresetApplicable(preset, collectionData);
			
			if (!isApplicable) {
				button.disabled = true;
				button.classList.add('disabled');
			}
		});
	}

	// NEW: Get collection data for filtering
	getCollectionData() {
		// Get collection info from the page
		const grid = document.querySelector('.atelier-grid');
		const totalResults = parseInt(grid?.dataset.totalResults || 0);
		const currentUrl = window.location.pathname;
		
		return {
			totalResults,
			currentUrl,
			// Add more collection data as needed
		};
	}

	// NEW: Check if a preset is applicable
	isPresetApplicable(preset, collectionData) {
		switch(preset) {
			case 'under-100':
				// Check if any products are under $100
				// This would need server-side data, but for now we'll assume it's applicable
				return true;
				
			case 'gold-only':
				// Check if collection has gold items
				const hasGold = document.querySelector('.atelier-filter-checkbox input[value="gold"]');
				return !!hasGold;
				
			case 'diamond-gold':
				// Check if collection has both diamond and gold
				const hasDiamond = document.querySelector('.atelier-filter-checkbox input[value="diamond"]');
				const hasGold2 = document.querySelector('.atelier-filter-checkbox input[value="gold"]');
				return !!(hasDiamond && hasGold2);
				
			case 'new-arrivals':
			case 'best-selling':
			case 'price-low-high':
				// These are always applicable as they're sort options
				return true;
				
			default:
				return true;
		}
	}

	applyPreset(presetName) {
		// Set this preset as active
		this.setActivePreset(presetName);
		
		// Clear existing filters first
		this.clearFilters();
		
		switch(presetName) {
			case 'under-100':
				// Set price range to 0-100
				const priceRange = document.querySelector('.atelier-price-range');
				if (priceRange) {
					priceRange.value = Math.min(100, priceRange.max);
				}
				break;
				
			case 'gold-only':
				// Check only gold material
				const goldCheckbox = document.querySelector('.atelier-filter-checkbox input[value="gold"]');
				if (goldCheckbox) {
					goldCheckbox.checked = true;
				}
				break;
				
			case 'diamond-gold':
				// Check diamond stone and gold material
				const diamondCheckbox = document.querySelector('.atelier-filter-checkbox input[value="diamond"]');
				const goldCheckbox2 = document.querySelector('.atelier-filter-checkbox input[value="gold"]');
				if (diamondCheckbox) diamondCheckbox.checked = true;
				if (goldCheckbox2) goldCheckbox2.checked = true;
				break;
				
			case 'new-arrivals':
				// Set sort to newest first
				this.currentSort = 'created-descending';
				this.applySort();
				return; // Don't apply filters for sort-only presets
				
			case 'best-selling':
				// Set sort to best selling
				this.currentSort = 'best-selling';
				this.applySort();
				return; // Don't apply filters for sort-only presets
				
			case 'price-low-high':
				// Set sort to price low to high
				this.currentSort = 'price-ascending';
				this.applySort();
				return; // Don't apply filters for sort-only presets
		}
		
		// Update filter count
		this.updateFilterCount();
		
		// Apply the filters and close sidebar
		this.applyFiltersWithClose();
	}

	// NEW: Apply filters and close sidebar (for presets)
	applyFiltersWithClose() {
		const filters = this.getSelectedFilters();
		const url = this.buildFilterUrl(filters);
		
		this.showFilterLoading();
		
		fetch(url)
			.then(response => response.text())
			.then(html => {
				const parser = new DOMParser();
				const doc = parser.parseFromString(html, 'text/html');
				
				// Update grid
                const newGrid = doc.querySelector('.atelier-grid');
                const currentGrid = document.querySelector('.atelier-grid');
                if (newGrid && currentGrid) {
                    currentGrid.innerHTML = newGrid.innerHTML;
                    // Update data attributes
                    const totalPages = newGrid.dataset.totalPages;
                    const totalResults = newGrid.dataset.totalResults;
                    if (totalPages) currentGrid.dataset.totalPages = totalPages;
                    if (totalResults) currentGrid.dataset.totalResults = totalResults;
                    // Clear any prior empty state and ensure grid is visible
                    document.querySelectorAll('.atelier-empty').forEach(n => n.remove());
                    currentGrid.style.display = '';
                    const endMsgNode = document.querySelector('.atelier-end-message');
                    if (endMsgNode) endMsgNode.style.display = 'none';
                } else if (!newGrid && currentGrid) {
                    document.querySelectorAll('.atelier-empty').forEach(n => n.remove());
                    const empty = document.createElement('div');
                    empty.className = 'atelier-empty';
                    empty.innerHTML = '<p>No products found.</p>';
                    const endMsg = document.querySelector('.atelier-end-message');
                    if (endMsg && endMsg.parentNode) {
                        endMsg.parentNode.insertBefore(empty, endMsg);
                        const p = endMsg.querySelector('p');
                        if (p) p.textContent = "You've viewed all 0 products in this collection";
                        endMsg.style.display = 'block';
                    }
                    currentGrid.style.display = 'none';
                    const loadMore = document.querySelector('.atelier-load-more');
                    if (loadMore) loadMore.style.display = 'none';
                }
				
                // Phase 7 / P7-C7: H1 .results-count stays at the server-rendered
                // unfiltered total (collection.all_products_count). Don't overwrite
                // it from AJAX responses — the chip + floating pill carry the
                // filtered-context numbers.
				
				// Update filter results count
				const filteredCount = parseInt(newGrid?.dataset.totalResults || 0);
				this.updateFilterResultsCount(filteredCount, this.originalTotalCount);
				// Announce update for a11y
				const announce = document.getElementById('atelier-announce');
				if (announce) {
					announce.textContent = filteredCount > 0 ? `Updated: ${filteredCount} results` : 'No products found';
				}
				
				// Update URL without page reload
				window.history.pushState({}, '', url);
				
				// Reset infinite scroll. Same fix as in applyUrl/handlePayload:
				// `resetInfiniteScroll` is a method on this (AtelierFilterBar),
				// not on the global InfiniteScroll instance. The previous call
				// threw silently and skipped hideFilterLoading + sidebar close.
				this.resetInfiniteScroll();
				
				this.hideFilterLoading();
				
				// Force close sidebar
				if (this.isMobile()) {
					this.closeSidebar();
					// Smooth scroll to grid top
					const grid2 = document.querySelector('.atelier-grid');
					if (grid2 && typeof grid2.scrollIntoView === 'function') {
						grid2.scrollIntoView({ behavior: 'smooth', block: 'start' });
					} else {
						window.scrollTo({ top: 0, behavior: 'smooth' });
					}
				}
				
				// Update filter count after applying filters
				this.updateFilterCount();
				// Phase 7 / P7-C9: refresh Clear-button enabled state +
				// Apply button label with the new filtered count.
				this.updateClearButtonState();
				const filteredNow2 = parseInt(document.querySelector('.atelier-grid')?.dataset.totalResults || 0);
				this.updateApplyButtonLabel(filteredNow2);
			})
			.catch(error => {
				console.error('Error applying filters:', error);
				this.hideFilterLoading();
			});
	}

	// Phase 7 / P7-C9: legacy helpers kept as no-op shims. The Apply
	// button is now always enabled (it acts as a close-drawer action),
	// and Clear handles its own enabled-when-dirty state via
	// updateClearButtonState(). Anything still calling these from older
	// code paths just gets a no-op.
	enableApplyButton() {
		// Preserve the "selecting a manual filter clears any active preset"
		// side effect — that's still useful UX even in live mode.
		const hasActiveFilters = this.getActiveFilterCount() > 0;
		if (hasActiveFilters && this.activePreset) {
			this.clearActivePreset();
		}
	}

	disableApplyButton() {
		// no-op (kept for backwards compat with older call sites)
	}

	// NEW: Set active preset state
	setActivePreset(presetName) {
		// Remove active state from all presets
		const allPresets = document.querySelectorAll('.atelier-preset-btn');
		allPresets.forEach(btn => {
			btn.classList.remove('active');
		});
		
		// Set active state on current preset
		if (presetName) {
			const activePreset = document.querySelector(`[data-preset="${presetName}"]`);
			if (activePreset) {
				activePreset.classList.add('active');
			}
		}
		
		this.activePreset = presetName;
	}

	// NEW: Clear active preset state
	clearActivePreset() {
		const allPresets = document.querySelectorAll('.atelier-preset-btn');
		allPresets.forEach(btn => {
			btn.classList.remove('active');
		});
		this.activePreset = null;
	}
}


document.addEventListener('DOMContentLoaded', () => {
	if (isAtelierInfiniteEnabled()) {
		window.atelierInfiniteScroll = new AtelierInfiniteScroll();
		window.atelierInfiniteScroll.init();
	} else {
		window.atelierInfiniteScroll = null;
	}
	new AtelierSubcollectionCarousel().init();
	window.atelierFilterBar = new AtelierFilterBar();
	// Phase 7 / P7-D2 (May 4, 2026): AtelierSwatchHoverSwap moved to
	// `assets/atelier_swatch.js.liquid` (shared with the search PLP).
	// That file auto-initializes on its own DOMContentLoaded.
});
